import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Avatar, Menu, MenuItem, Divider, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { AccountCircle, Logout, Person } from '@mui/icons-material';
import { getSession, clearSession, getAutoToken } from '../../utils/authUtils';
import Api from '../../services/api';
import tokenManager from '../../auth/TokenManager';
import AutoTokenManager from '../../utils/autoTokenUtils';

import logger from '../../utils/logger';
const DropdownContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center'
}));

const UserButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  backgroundColor: '#ffffff',
  border: '2px solid #0B91D6',
  borderRadius: '50%',
  width: '44px',
  height: '44px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9fa',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(11, 145, 214, 0.2)'
  }
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
  width: '28px',
  height: '28px',
  background: 'linear-gradient(135deg, #0B91D6 0%, #2563EB 100%)',
  boxShadow: '0 2px 8px rgba(11, 145, 214, 0.3)',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    marginTop: '8px',
    minWidth: '200px',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
    border: '1px solid #e5e7eb',
    overflow: 'hidden'
  }
}));

const MenuHeader = styled(Box)(({ theme }) => ({
  padding: '16px 20px',
  backgroundColor: '#f8f9fa',
  borderBottom: '1px solid #e5e7eb'
}));

const UserName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  marginBottom: '4px'
}));

const UserEmail = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  color: '#6b7280'
}));

const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  padding: '12px 20px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  color: '#374151',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#f3f4f6',
    color: '#0B91D6'
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
    color: '#6b7280'
  },
  '&:hover .MuiSvgIcon-root': {
    color: '#0B91D6'
  }
}));

const LogoutMenuItem = styled(StyledMenuItem)(({ theme }) => ({
  color: '#dc2626',
  '&:hover': {
    backgroundColor: '#fef2f2',
    color: '#dc2626'
  },
  '& .MuiSvgIcon-root': {
    color: '#dc2626'
  },
  '&:hover .MuiSvgIcon-root': {
    color: '#dc2626'
  }
}));

const UserAccountDropdown = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const navigate = useNavigate();
  const open = Boolean(anchorEl);

  useEffect(() => {
    const session = getSession();
    logger.log('[UserAccountDropdown] Session data:', session);
    
    if (session) {
      // Extract email first as it's most reliable
      const userEmail = session.email ||
        session.user?.email ||
        session.userEmail ||
        session.user?.userEmail ||
        session.googleEmail ||
        session.user?.googleEmail ||
        'user@example.com';
      
      // Build full name from first + last name if available
      let userName = '';
      const firstName = session.first_name || session.firstName || session.user?.first_name || session.user?.firstName || '';
      const lastName = session.last_name || session.lastName || session.user?.last_name || session.user?.lastName || '';
      
      if (firstName || lastName) {
        userName = `${firstName} ${lastName}`.trim();
      }
      
      // If no name from first/last, try other sources
      if (!userName) {
        userName = session.name ||
          session.user?.name ||
          session.user?.displayName ||
          session.displayName ||
          session.userName ||
          session.user?.userName ||
          session.googleDisplayName ||
          session.user?.googleDisplayName ||
          session.full_name ||
          session.user?.full_name ||
          '';
      }
      
      // If still no name, extract from email
      if (!userName && userEmail && userEmail !== 'user@example.com') {
        userName = userEmail.split('@')[0].replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      }
      
      // Final fallback
      if (!userName) {
        userName = 'User';
      }
      
      // Extract account/business name for display
      const accountName = session.gmbPrimaryAccountName ||
        session.gmbAccounts?.[0]?.accountName ||
        session.businessName ||
        session.user?.accountName ||
        session.accountName ||
        userName;
      
      logger.log('[UserAccountDropdown] Extracted user info:', {
        userName,
        userEmail,
        accountName,
        firstName,
        lastName
      });
      
      setUserInfo({
        name: userName,
        accountName: accountName,
        email: userEmail,
        initials: getInitials(userName)
      });
    }
  }, []);

  const getDisplayNameFromSession = (session) => {
    const candidates = [
      session.accountName,
      session.businessName,
      session.account?.name,
      session.business?.name,
      session.organization?.name,
      session.companyName,
      session.user?.accountName,
      session.user?.businessName,
      session.user?.name,
      session.user?.displayName,
    ].filter(Boolean);
    return (candidates[0] || 'Account');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getLogoFromSession = (session) => {
    const candidates = [
      session.logoUrl,
      session.logo,
      session.photoURL,
      session.profileImage,
      session.imageUrl,
      session.business?.logoUrl,
      session.account?.logoUrl,
      session.organization?.logoUrl,
      session.user?.photoURL,
      session.user?.avatar,
      session.user?.imageUrl
    ].filter(Boolean);
    return candidates[0] || null;
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMyAccount = () => {
    handleClose();
    navigate('/my-account');
  };

  const handleSettings = () => {
    handleClose();
    navigate('/');
  };


  const handleLogout = async () => {
    const confirmLogout = window.confirm('Are you sure you want to logout?');
    if (!confirmLogout) {
      logger.log('Logout cancelled by user.');
      return;
    }
    try {
      AutoTokenManager.stopAutoRefresh();
      const session = getSession();
      let token = getAutoToken();
      if (!token && session) {
        token = session.token || session.access_token || session.accessToken || session.authToken || session.bearer_token || session.user?.token;
      }
      const refreshToken = session?.refresh_token || session?.refreshToken || localStorage.getItem('refresh_token') || localStorage.getItem('googleRefreshToken');
      const toBearer = (raw) => {
        if (!raw || typeof raw !== 'string') return null;
        const t = raw.trim();
        return /^Bearer\s+/i.test(t) ? t : `Bearer ${t}`;
      };

      try {
        if (!token || String(token).trim().length === 0) {
          logger.warn('[Logout] No token available to send to backend. Skipping server logout.');
          throw new Error('no_token');
        }
        let res = await Api.request('/customer/auth/logout', {
          method: 'POST',
          noAuth: true,
          headers: {
            'Content-Type': 'application/json',
            Authorization: token || ''
          },
          body: JSON.stringify({
            refreshToken: refreshToken || undefined,
            refresh_token: refreshToken || undefined,
            revokeAll: true
          })
        });
        logger.log('Successfully logged out from server');
      } catch (e) {
        try {
          await Api.request('/customer/auth/logout', {
            method: 'POST',
            noAuth: true,
            headers: {
              'Content-Type': 'application/json',
              Authorization: token ? `Bearer ${token}` : ''
            },
            body: JSON.stringify({
              refreshToken: refreshToken || undefined,
              refresh_token: refreshToken || undefined,
              revokeAll: true
            })
          });
          logger.log('Successfully logged out from server (fallback with Bearer)');
        } catch (e2) {
          logger.warn('Logout API call failed (both raw and Bearer). Continuing with local logout');
        }
      }
    } catch (error) {
      logger.error('Error calling logout API:', error);
    }

    try {
      tokenManager.remove('google');
      logger.log('Google tokens cleared from TokenManager');
    } catch (clearError) {
      logger.error('Error clearing Google tokens:', clearError);
    }

    clearSession();

    // Clear all monthly plan caches on logout
    try {
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith('monthly_plan_') || key.startsWith('weekly_products_services_'))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`[Logout] Cleared ${keysToRemove.length} monthly plan cache entries`);
    } catch (e) {
      console.warn('[Logout] Failed to clear monthly plan caches:', e);
    }

    navigate('/login');
  };

  if (!userInfo) {
    return null;
  }

  return (
    <DropdownContainer>
      <UserButton
        onClick={handleClick}
        aria-controls={open ? 'user-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        {userInfo.logoUrl ? (
          <UserAvatar src={userInfo.logoUrl} alt={userInfo.name} />
        ) : (
          <UserAvatar>
            <AccountCircle sx={{ color: '#ffffff' }} />
          </UserAvatar>
        )}
      </UserButton>

      <StyledMenu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'user-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuHeader>
          <UserName>{userInfo.accountName}</UserName>
          <UserEmail>{userInfo.email}</UserEmail>
        </MenuHeader>
        <Divider sx={{ margin: '8px 0' }} />
        <LogoutMenuItem onClick={handleLogout}>
          <Logout />
          Logout
        </LogoutMenuItem>
      </StyledMenu>
    </DropdownContainer>
  );
};

export default UserAccountDropdown;
